import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Button,
  TextField,
  Input,
  Alert,
  Grid,
  Divider,
  IconButton,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Pending,
  Dangerous,
  DoneAll,
  CloudUpload,
  Delete
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../../request/http-common';

const TransactionDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentProof, setPaymentProof] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  // Get transaction from location state or fetch from API
  const [transaction, setTransaction] = useState(location.state?.transaction || null);

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    currencySign: 'standard' 
  }).format(amount);

  const addressText = (address) => {
    let text = String(address) || address;
    let last6 = text.slice(text.length - 6);
    let first3 = text.substring(0, 6);
    return first3 + '***' + last6;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'successful':
      case 'paid':
        return '#37dc8a';
      case 'pending':
      case 'awaiting confirmation':
        return '#f7a21d';
      case 'processing':
        return '#fcaa00';
      case 'declined':
        return '#dd0a22';
      case 'completed':
        return '#ccc6c6';
      default:
        return '#f7a21d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'successful':
      case 'paid':
        return <CheckCircle />;
      case 'pending':
      case 'awaiting confirmation':
      case 'processing':
        return <Pending />;
      case 'declined':
        return <Dangerous />;
      case 'completed':
        return <DoneAll />;
      default:
        return <Pending />;
    }
  };

  // Fetch transaction details if not available
  const { data: fetchedTransaction, isLoading } = useQuery(
    ['transaction', id],
    async () => {
      const response = await apiClient.get(`/api/transaction/${id}`);
      return response.data;
    },
    {
      enabled: !transaction && !!id,
      onSuccess: (data) => {
        setTransaction(data.transaction);
        if (data.payment_proof) {
          setUploadedFile(data.payment_proof);
        }
      }
    }
  );

  // Upload payment proof mutation
  const { mutate: uploadPaymentProof, isLoading: isUploading } = useMutation(
    async (formData) => {
      return await apiClient.post(`/api/transaction/${id}/payment-proof`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onSuccess: (response) => {
        setUploadSuccess('Payment proof uploaded successfully!');
        setUploadedFile(response.data.payment_proof);
        setPaymentProof(null);
        setUploadError('');
      },
      onError: (error) => {
        setUploadError(error.response?.data?.message || 'Failed to upload payment proof');
        setUploadSuccess('');
      }
    }
  );

  // Delete payment proof mutation
  const { mutate: deletePaymentProof } = useMutation(
    async () => {
      return await apiClient.delete(`/api/transaction/${id}/payment-proof`);
    },
    {
      onSuccess: () => {
        setUploadedFile(null);
        setUploadSuccess('Payment proof deleted successfully!');
        setUploadError('');
      },
      onError: (error) => {
        setUploadError(error.response?.data?.message || 'Failed to delete payment proof');
      }
    }
  );

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        setUploadError('Please upload only JPG, PNG, or PDF files');
        return;
      }

      if (file.size > maxSize) {
        setUploadError('File size must be less than 5MB');
        return;
      }

      setPaymentProof(file);
      setUploadError('');
    }
  };

  const handleUpload = () => {
    if (!paymentProof) return;

    const formData = new FormData();
    formData.append('payment_proof', paymentProof);
    uploadPaymentProof(formData);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading transaction details...</Typography>
      </Box>
    );
  }

  if (!transaction) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Transaction not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          Transaction Details
        </Typography>
      </Box>

      {/* Transaction Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar 
              sx={{ 
                bgcolor: getStatusColor(transaction.status), 
                mr: 2,
                width: 48,
                height: 48
              }}
            >
              {getStatusIcon(transaction.status)}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {transaction?.after_block || transaction.gateway || transaction.coin_name || 'Transaction'}
              </Typography>
              <Chip 
                label={transaction.status || 'Unknown'} 
                color={
                  ['approved', 'successful', 'paid'].includes(transaction.status?.toLowerCase()) 
                    ? 'success' 
                    : ['pending', 'awaiting confirmation'].includes(transaction.status?.toLowerCase())
                    ? 'warning'
                    : 'error'
                }
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {transaction.coin_name && '-'} {formatPrice(transaction.local_amount || transaction.amount)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Transaction ID
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {addressText(transaction?.uuid || transaction.transaction_id || transaction.address)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2">
                {new Date(transaction.created_at).toLocaleDateString()} {new Date(transaction.created_at).toLocaleTimeString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Method
              </Typography>
              <Typography variant="body2">
                {transaction.gateway || transaction.coin_name || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment Proof Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Payment Proof
          </Typography>

          {/* Display uploaded file */}
          {uploadedFile && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle2">
                    {uploadedFile.original_name || 'Payment Proof'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uploaded on: {new Date(uploadedFile.created_at || Date.now()).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    href={uploadedFile.file_path}
                    target="_blank"
                    sx={{ mr: 1 }}
                  >
                    View
                  </Button>
                  <IconButton 
                    color="error" 
                    size="small"
                    onClick={() => deletePaymentProof()}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Upload new file */}
          {!uploadedFile && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Upload proof of payment for this transaction (JPG, PNG, or PDF, max 5MB)
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Input
                  type="file"
                  onChange={handleFileChange}
                  inputProps={{
                    accept: '.jpg,.jpeg,.png,.pdf',
                  }}
                  sx={{ display: 'none' }}
                  id="payment-proof-upload"
                />
                <label htmlFor="payment-proof-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    fullWidth
                  >
                    Choose File
                  </Button>
                </label>

                {paymentProof && (
                  <Box>
                    <Typography variant="body2" mb={1}>
                      Selected: {paymentProof.name}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleUpload}
                      disabled={isUploading}
                      fullWidth
                    >
                      {isUploading ? 'Uploading...' : 'Upload Payment Proof'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Success/Error Messages */}
          {uploadSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadSuccess}
            </Alert>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TransactionDetails;